import { useMutation } from '@apollo/client';
import { ButtonSaveType } from '@components/ButtonSaveSplit';
import { ADD_TALENT_LANGUAGE, UPDATE_TALENT_LANGUAGE } from '@gql/talentLanguages';
import { GET_TALENT } from '@gql/talents';
import { isEmpty } from 'lodash';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { LanguageFormValues, TalentLanguage } from '../types';
import { getDefaultFormValues } from '../utils/languageUtils';

interface UseLanguageFormOptions {
    selectedData: TalentLanguage | null;
    setOpenForm: (open: boolean) => void;
    setSelectedData: (data: TalentLanguage | null) => void;
}

/**
 * Encapsulates all form state, mutation logic, and submission handling
 * for the language add/edit form.
 */
export const useLanguageForm = ({ selectedData, setOpenForm, setSelectedData }: UseLanguageFormOptions) => {
    const { showSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [saveType, setSaveType] = useState<ButtonSaveType>(ButtonSaveType.SAVE_AND_RETURN);
    const [randomKey, setRandomKey] = useState(Math.random());
    const [hasCertificate, setHasCertificate] = useState(
        !!selectedData?.languages?.[0]?.certificates?.[0]?._id,
    );

    const defaultValues = getDefaultFormValues(selectedData);
    const form = useForm<LanguageFormValues>({
        mode: 'onChange',
        defaultValues,
    });

    const [save] = useMutation(ADD_TALENT_LANGUAGE);
    const [updateLanguage] = useMutation(UPDATE_TALENT_LANGUAGE);

    const onSubmit = async (inputValues: LanguageFormValues) => {
        setIsLoading(true);

        const certificateLanguages = (inputValues.certificateLanguages ?? []).filter(Boolean);

        // Note: `certificateLanguges` is the field name expected by the API (server-side typo preserved)
        const body = {
            request: {
                id: selectedData?._id,
                languages: [
                    {
                        languageId: inputValues.language ?? null,
                        levelId: inputValues.level ?? null,
                        certificateLanguges: !isEmpty(certificateLanguages) ? certificateLanguages : [],
                    },
                ],
                certificateInput: {
                    issuer: inputValues.issuer ?? null,
                    title: inputValues.title ?? null,
                    number: inputValues.number ?? null,
                    url: inputValues.url ?? null,
                    obtainedResult: (inputValues.obtainedResult ?? '').toString(),
                    startDate: inputValues.startDate ?? null,
                    endDate: inputValues.endDate ?? null,
                    file: inputValues.file ?? null,
                },
            },
        };

        if (!hasCertificate) {
            delete (body.request as Partial<typeof body.request>).certificateInput;
        }

        try {
            const refetchQueries = () => [
                { query: GET_TALENT, variables: { talentId: selectedData?.talentId } },
            ];

            if (!selectedData?._id) {
                await save({ variables: body, refetchQueries });
                showSnackbar('Langue ajoutée avec succès', 'success');
            } else {
                await updateLanguage({ variables: body, refetchQueries });
                showSnackbar('Langue modifiée avec succès', 'success');
            }

            if (saveType === ButtonSaveType.SAVE_AND_RETURN) {
                form.reset();
                setOpenForm(false);
                setSelectedData(null);
            } else {
                form.reset(getDefaultFormValues(null));
                setSelectedData(null);
                setRandomKey(Math.random());
            }
        } catch {
            showSnackbar('Une erreur est survenue', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return {
        form,
        isLoading,
        saveType,
        setSaveType,
        randomKey,
        hasCertificate,
        setHasCertificate,
        onSubmit,
        defaultValues,
    };
};
